/* eslint-disable max-lines */
import get from 'lodash/get'
import isEmpty from 'lodash/isEmpty'
import camelCase from 'camel-case'
import { ConfigManager, COUNTRY } from '~/managers/ConfigManager'
import {
  Map,
  LABEL_TYPES,
  LabelEntity,
  LabelTypes,
  ErrorContent,
  TransactionsTableColumnEntity,
  EvidenceType,
} from '~/types'

export const getCurrencySymbol = () => {
  return ConfigManager.getCountry() === COUNTRY.INDONESIA ? 'Rp' : 'S$'
}

export function formatAmountToLocalCurrency(amount?: string) {
  if (!amount) {
    return '-'
  }

  const amountFloat = parseFloat(amount)

  if (Number.isNaN(amountFloat) || amountFloat === 0) {
    return '-'
  }

  const idFormatter = new Intl.NumberFormat('id', {
    style: 'currency',
    currency: 'IDR',
  })
  const sgFormatter = new Intl.NumberFormat('sg', {
    style: 'currency',
    currency: 'SGD',
  })

  const result =
    ConfigManager.getCountry() === COUNTRY.INDONESIA
      ? idFormatter.format(amountFloat)
      : sgFormatter
          .format(amountFloat)
          .replace('SGD', 'S$')
          .replace(' ', '')

  return result
}

export function convertNetworkToLabel(network: string | undefined) {
  switch (network) {
    case 'ethereum':
      return 'ERC20'
    case 'zilliqa':
      return 'ZRC2'
  }
  return network
}

export function getType(status: string, entity: LabelEntity) {
  const STATUS_MAP: Map<LabelEntity> = {
    transaction: {
      completed: LABEL_TYPES.SUCCESS,
      pending: LABEL_TYPES.WARNING,
      processing: LABEL_TYPES.WARNING,
      paid: LABEL_TYPES.SUCCESS,
      failed: LABEL_TYPES.ERROR,
      cancelled: LABEL_TYPES.ERROR,
      refunded: LABEL_TYPES.NEUTRAL,
      expired: LABEL_TYPES.ERROR,
    },
    verification: {
      initial: LABEL_TYPES.NEUTRAL,
      pending: LABEL_TYPES.WARNING,
      rejected: LABEL_TYPES.ERROR,
      verified: LABEL_TYPES.SUCCESS,
      unverified: LABEL_TYPES.NEUTRAL,
      'permanently rejected': LABEL_TYPES.ERROR,
    },
    invoice: {
      draft: LABEL_TYPES.NEUTRAL,
      unpaid: LABEL_TYPES.WARNING,
      paid: LABEL_TYPES.SUCCESS,
      settled: LABEL_TYPES.SUCCESS,
      expired: LABEL_TYPES.ERROR,
    },
    developerTools: {
      enabled: LABEL_TYPES.SUCCESS,
      disabled: LABEL_TYPES.ERROR,
    },
    blockchainAddress: {
      rejected: LABEL_TYPES.ERROR,
      verified: LABEL_TYPES.SUCCESS,
      custodialpending: LABEL_TYPES.WARNING,
      noncustodialpending: LABEL_TYPES.NEUTRAL,
    },
    bankAccount: {
      pending: LABEL_TYPES.WARNING,
      rejected: LABEL_TYPES.ERROR,
      verified: LABEL_TYPES.SUCCESS,
      unverified: LABEL_TYPES.NEUTRAL,
    },
    xfersDash: {
      success: LABEL_TYPES.SUCCESS,
      pending: LABEL_TYPES.WARNING,
      failed: LABEL_TYPES.ERROR,
    },
    creditCardsPayments: {
      authorized: LABEL_TYPES.WARNING,
      voided: LABEL_TYPES.ERROR,
      'partially captured': LABEL_TYPES.WARNING,
      captured: LABEL_TYPES.SUCCESS,
      'partially refunded': LABEL_TYPES.WARNING,
      refunded: LABEL_TYPES.SUCCESS,
      disputed: LABEL_TYPES.NEUTRAL,
    },
    creditCardsDisputes: {
      'needs response': LABEL_TYPES.WARNING,
      'under review': LABEL_TYPES.INFO,
      resolved: LABEL_TYPES.SUCCESS,
      won: LABEL_TYPES.SUCCESS,
      lost: LABEL_TYPES.ERROR,
      canceled: LABEL_TYPES.ERROR,
      expired: LABEL_TYPES.ERROR,
      accepted: LABEL_TYPES.SUCCESS,
    },
  }

  return STATUS_MAP[entity][status.toLowerCase()] as LabelTypes
}

export function getError(errorCode: string) {
  const ERROR_CONTENT: ErrorContent = {
    '400': {
      errorTitle: '400',
      errorSubtitle: 'Bad request',
      errorContent: 'Your request resulted in an error.',
    },
    '401': {
      errorTitle: '401',
      errorSubtitle: 'No authorization found',
      errorContent: 'You do not have permission to access this site.',
    },
    '403': {
      errorTitle: '403',
      errorSubtitle: 'Forbidden',
      errorContent: 'An error occurred when we tried to process your request.',
    },
    '404': {
      errorTitle: '404',
      errorSubtitle: 'Page Not Found',
    },
    '408': {
      errorTitle: '408',
      errorSubtitle: 'Request time-out',
      errorContent: "Your browser didn't send a complete request this time.",
    },
    '500': {
      errorTitle: '500',
      errorSubtitle: 'Internal server error',
      errorContent: "Your browser didn't send a complete request this time.",
    },
    'Access denied': {
      errorTitle: 'Oops!',
      errorSubtitle: 'Access denied',
      errorContent: 'You do not have the permission to view the page.',
    },
  }

  return ERROR_CONTENT[errorCode]
}

export function formatAccountNumberWithDashes(value: string) {
  // 10000100000010176 -> 1000-0100-0000-1017-6
  if (value.length === 17) {
    const splitAry = value.match(/.{1,4}/g) // split every 4 character into array ['1000', '0100', '0000', '1017', '6']
    if (splitAry === null) return value
    return splitAry.join('-') // join first 5 element with '-'
  } else return value
}

export function formatAmount(amount: string | number | undefined) {
  if (amount === '' || amount === undefined) {
    return '-'
  }
  const amountFloat = typeof amount === 'string' ? parseFloat(amount) : amount
  if (Number.isNaN(amountFloat) || amountFloat === 0) {
    return '-'
  }
  return amountFloat
}

export function formatSeconds(secondNum: number | undefined) {
  if (secondNum === undefined || Number.isNaN(secondNum)) {
    return '-'
  }
  const minutes = Math.floor(secondNum / 60)
  const seconds = secondNum - minutes * 60
  let minutesStr = minutes.toString()
  let secondsStr = seconds.toString()
  if (minutes < 10) {
    minutesStr = '0' + minutes
  }
  if (seconds < 10) {
    secondsStr = '0' + seconds
  }
  return minutesStr + ':' + secondsStr
}

export const formatAddressLabel = (string: string, stringLength: number) => {
  // Kgobp94xUnTL6FsmI3XInFB73e -> Kgobp94xUnTL6Fsm...

  if (string.length > stringLength) {
    return string.substring(0, stringLength) + '...'
  }

  return string
}

/* eslint-disable @typescript-eslint/no-explicit-any */
export const isObject = (obj: any) => {
  return obj === Object(obj) && !Array.isArray(obj) && typeof obj !== 'function'
}

/* eslint-disable @typescript-eslint/no-explicit-any */
export const keysToCamel = (obj: any): any => {
  if (isObject(obj)) {
    const n: any = {}

    Object.keys(obj).forEach(k => {
      n[camelCase(k)] = keysToCamel(obj[k])
    })

    return n
  } else if (Array.isArray(obj)) {
    return obj.map(i => {
      return keysToCamel(i)
    })
  }

  return obj
}

type Account = {
  bankAbbrev: string
  accountNo?: string
  virtualAccountNumber?: string
}

export const formatBankAccountName = (account: Account) => {
  const formattedAccountName = get(account, 'bankAbbrev', '-').toUpperCase()
  const isVirtualAccountExists = !isEmpty(account.virtualAccountNumber)
  const isCommonAccountExists = !isEmpty(account.accountNo)
  let accountNumber
  let accountName = formattedAccountName

  if (isVirtualAccountExists) {
    accountNumber = account.virtualAccountNumber
    accountName = `VA ${formattedAccountName}`
  } else if (isCommonAccountExists) {
    accountNumber = account.accountNo
  } else {
    accountNumber = '-'
  }

  return { accountName, accountNumber }
}

export const formatPaymentMethod = (paymentMethod: string | undefined) => {
  let formattedPaymentMethod

  if (paymentMethod === 'va') {
    formattedPaymentMethod = 'Virtual Account'
  } else if (paymentMethod === 'paynow') {
    formattedPaymentMethod = 'PayNow'
  } else {
    formattedPaymentMethod = '-'
  }

  return formattedPaymentMethod
}

export const TwoFaHelpMessage = (message: string | undefined) => {
  let helpMessage
  if (message) {
    if (message.includes('Invalid OTP provided')) {
      helpMessage = 'Authentication code is incorrect. Please try again.'
    } else {
      helpMessage = message
    }
    return helpMessage
  }
  return helpMessage
}

export const getXfersAcceptTabNames = (tabKey: string) => {
  let tabName: string = ''
  let searchTooltip: string = ''
  let placeholder: string = ''

  switch (tabKey) {
    case 'virtual_accounts':
      tabName = 'Virtual Account'
      searchTooltip = 'Search by Ref. ID or Virtual Account Number'
      placeholder = 'Search by Ref. ID or VA No.'
      break
    case 'qris':
      tabName = 'QRIS'
      searchTooltip = 'Search by Ref. ID or Display Name'
      break
    case 'e_wallet':
      tabName = 'E-Wallet'
      searchTooltip = 'Search by Ref. ID or E-Wallet Type'
      break
    case 'retail':
      tabName = 'Retail'
      searchTooltip = 'Search by Ref. ID or Retail Code'
      break
    case 'cashin':
      tabName = 'Cashin'
      searchTooltip = 'Search by Ref. ID or Retail Code'
  }
  return { tabName, searchTooltip, placeholder }
}

export const getDisbursementFeatureName = () => {
  return ConfigManager.isRegionalSite() ? 'Payout Form' : 'Xfers Dash'
}

export const getColumnName = (columnName: TransactionsTableColumnEntity) => {
  const COLUMN_MAP: Map<TransactionsTableColumnEntity> = {
    createdDate: {
      columnTitleName: 'Created Date',
      columnTooltipTitle: 'The date & time when the payment is created.',
    },
    amount: {
      columnTitleName: 'Amount',
      columnTooltipTitle: 'Amount received before deductions from tax and fee are made.',
    },
    netAmount: {
      columnTitleName: 'Net Amount',
      columnTooltipTitle: 'Final amount after deductions from tax and fee are made.',
    },
    completedDate: {
      columnTooltipTitle: 'The date & time when the payment is completed.',
    },
  }

  return COLUMN_MAP[columnName]
}

export const formatDisputeStatus = (disputeStatus: string) => {
  let status

  switch (disputeStatus) {
    case 'evidence_required':
      status = 'Needs Response'
      break
    case 'evidence_under_review':
      status = 'Under Review'
      break
    case 'resolved':
      status = 'Resolved'
      break
    case 'won':
      status = 'Won'
      break
    case 'lost':
      status = 'Lost'
      break
    case 'canceled':
      status = 'Canceled'
      break
    case 'expired':
      status = 'Expired'
      break
    case 'accepted':
      status = 'Accepted'
  }

  return status
}

export const formatCreditCardNumber = (creditCardNumber: string) => {
  return `•••• ${creditCardNumber.slice(-4)}`
}

export const formatCreditCardType = (creditCardType: string) => {
  let cardType

  switch (creditCardType) {
    case 'visa':
      cardType = 'Visa'
      break
    case 'master_card':
      cardType = 'Master Card'
      break
    default:
      cardType = '-'
  }

  return cardType
}

export const getEvidenceType = (evidenceType: EvidenceType) => {
  const LIST_MAP: Map<EvidenceType> = {
    proofOfDelivery: {
      evidenceTitle: 'Proof of delivery or service',
      evidenceTooltip: 'Dummy proof of delivery or service tooltip',
    },
    invoiceOrReceipt: {
      evidenceTitle: 'Invoice or receipt',
      evidenceTooltip: 'Dummy invoice or receipt tooltip',
    },
    disticntTxnInvoice: {
      evidenceTitle: 'Invoice showing distinct transactions',
      evidenceTooltip: 'Dummy invoice showing distinct transactions tooltip',
    },
    customerCommutation: {
      evidenceTitle: 'Customer communication',
      evidenceTooltip: 'Dummy customer communication tooltip',
    },
    refundPolicy: {
      evidenceTitle: 'Refund or cancellation policy',
      evidenceTooltip: 'Dummy Refund or cancellation policy tooltip',
    },
    recurringTxn: {
      evidenceTitle: 'Recurring transaction agreement',
      evidenceTooltip: 'Dummy recurring transaction agreement tooltip',
    },
    additionalEvidence: {
      evidenceTitle: 'Additional evidence',
      evidenceTooltip: 'Dummy additional evidence tooltip',
    },
  }

  return LIST_MAP[evidenceType]
}
